import { LoadingButton } from '@mui/lab';
import {
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  dialogClasses,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  Radio,
  RadioGroup,
  SelectChangeEvent,
  Stack,
  styled,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { addTimeZone } from '@onevision-resources/date-utils';
import { getFullName } from '@provision/formatting-utils';
import { QueryBuilderMaterial } from '@react-querybuilder/material';
import dayjs from 'dayjs';

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { FullField, QueryBuilder, RuleGroupType } from 'react-querybuilder';

import { Frequency, Weekday } from 'rrule';
import { FormCheckbox } from 'src/components/Forms/FormCheckbox/form-checkbox';
import { FormDatePicker } from 'src/components/Forms/FormDatePicker/form-date-picker';
import { FormSelect } from 'src/components/Forms/FormSelect/form-select';
import {
  BehaviorTrigger,
  mapConditionToFormValues,
  mapUIRulesForRequest,
  RulesArray,
  SelectorValue,
} from 'src/graphql';
import type { ServerPlatformUserRaw } from 'src/graphql/entities/platform-user/platform-user';
import {
  useCreateSignalOnCallScheduleEvent,
  useDeleteSignalOnCallScheduleEvent,
  useUpdateSignalOnCallScheduleEvent,
} from 'src/graphql/entities/signal-oncall-schedule/hooks/signal-oncall-schedule';
import {
  DeleteRecurrence,
  EditRecurrence,
  type OncallManagementScheduleDataInput,
  type OncallManagementScheduleMutationRecord,
  type OncallManagementScheduleRecord,
  RecurrenceOptions,
} from 'src/graphql/entities/signal-oncall-schedule/signal-oncall-schedule.types';
import { RulesFormContainer } from 'src/pages/Settings/rules/rules-form';
import {
  conditionsCustomControls,
  muiComponents,
} from 'src/pages/Settings/rules/rules-form-config';
import { Modals, useModalMethods, useModalState } from 'src/store/modals';
import { useConfirmationModal } from 'src/utils/hooks';
import { useNotifications } from 'src/utils/hooks/use-notifications';
import {
  getValidConditionsCount,
  pruneIncompleteConditions,
} from 'src/utils/query-builder-conditions';
import { useScreenDimensions } from 'src/utils/system';

import {
  RECURRENCE_INTERVAL_OPTIONS,
  RECURRENCE_OPTIONS,
  RECURRENCE_PERIOD_OPTIONS,
  TIME_SLOT_OPTIONS,
  UNTIL_SLOTS,
  WEEKDAYS,
} from './helpers/const';
import {
  canEditCurrentEvent,
  getRecurrenceDays,
  getRecurrenceInterval,
  getRecurrencePeriod,
  isAllDaySchedule,
  isWeeklyPeriodSelected,
  mergeDateAndTime,
  TIME_FORMAT,
} from './helpers/dates-manipulation';
import { getRecurrenceUntil, mapScheduleToRruleString } from './helpers/events-data-mappers';
import { CalendarEventObject } from './types';
import { useSignalRulesEngine } from './use-signal-rules-engine';
import 'react-querybuilder/dist/query-builder.css';

export const StyledToggleButton = styled(ToggleButton, {
  name: 'StyledToggleButton',
  shouldForwardProp: (prop) => prop !== 'isMobile',
})<{ isMobile?: boolean }>(({ theme, isMobile }) => ({
  flex: 1,
  margin: theme.utils.important(0),
  minWidth: isMobile ? '3rem' : '4rem',
  '&.Mui-selected': {
    backgroundColor: theme.palette.secondary.main,
    color: theme.palette.common.white,
    '&:hover': {
      backgroundColor: theme.palette.secondary.dark,
    },
  },
  '&:hover': {
    backgroundColor: theme.palette.secondary.light,
    color: theme.palette.common.white,
  },
}));

const mapOptions = (options: { label: string; value: string | number }[]) =>
  options.map((it) => ({
    ...it,
    id: it.value.toString(),
  }));

enum SignalManagementFields {
  PrimaryAgentId = 'primaryAgentId',
  SecondaryAgentId = 'secondaryAgentId',
  DateStart = 'dateStart',
  TimeStart = 'timeStart',
  TimeEnd = 'timeEnd',
  Recurrence = 'recurrence',
  IsAllDay = 'isAllDay',
  RecurrenceInterval = 'recurrenceInterval',
  RecurrencePeriod = 'recurrencePeriod',
  EditType = 'editType',
  Conditions = 'conditions',
  EnabledConditions = 'enabledConditions',
}

export type SignalManagementFormValues = {
  [SignalManagementFields.PrimaryAgentId]: string;
  [SignalManagementFields.SecondaryAgentId]: string;
  [SignalManagementFields.DateStart]: dayjs.Dayjs;
  [SignalManagementFields.TimeStart]: string;
  [SignalManagementFields.TimeEnd]: string;
  [SignalManagementFields.Recurrence]: RecurrenceOptions;
  [SignalManagementFields.IsAllDay]: boolean;
  [SignalManagementFields.RecurrenceInterval]: string | undefined;
  [SignalManagementFields.RecurrencePeriod]: Frequency | undefined;
  [SignalManagementFields.EditType]: EditRecurrence | undefined;
  [SignalManagementFields.Conditions]: (RuleGroupType & { rules: RulesArray }) | undefined;
  [SignalManagementFields.EnabledConditions]: boolean;
};

export interface SignalOnCallScheduleModalProps {
  partnerId: number;
  events: OncallManagementScheduleRecord[];
  agents: ServerPlatformUserRaw[];
  timezone?: string;
  data: CalendarEventObject | null;
  onSubmit: (event: OncallManagementScheduleMutationRecord, isEditing?: boolean) => void;
  onDelete: (eventId: string) => void;
  onClose: () => void;
}

const validateOnCallSchedule = (values: SignalManagementFormValues) => {
  const startDate = !values[SignalManagementFields.IsAllDay]
    ? dayjs(
        mergeDateAndTime(
          values[SignalManagementFields.DateStart].toDate(),
          values[SignalManagementFields.TimeStart],
        ),
      )
    : dayjs(values[SignalManagementFields.DateStart]);

  if (!values[SignalManagementFields.IsAllDay]) {
    const endDate = dayjs(
      mergeDateAndTime(
        values[SignalManagementFields.DateStart].toDate(),
        values[SignalManagementFields.TimeEnd],
      ),
    );
    if (endDate.isBefore(startDate) || endDate.isSame(startDate)) {
      return {
        [SignalManagementFields.TimeStart]: ' ',
        [SignalManagementFields.TimeEnd]: 'End time should be later than start time',
      };
    }
  }

  const todayStartDay = dayjs().startOf('day').toDate();

  if (startDate.isBefore(todayStartDay, 'day')) {
    return {
      [SignalManagementFields.DateStart]: 'Start date should be in the future',
    };
  }
  return {};
};

const formDataToOnCallManagementScheduleDataInput = ({
  values,
  timezone,
  partnerId,
  recurringDays,
}: {
  values: SignalManagementFormValues;
  timezone: string;
  partnerId: number;
  recurringDays: Weekday[];
}): Omit<OncallManagementScheduleDataInput, 'oncallManagementScheduleId'> => {
  let start;
  let end;
  let recurrence = values[SignalManagementFields.Recurrence];

  if (!values[SignalManagementFields.IsAllDay]) {
    start = mergeDateAndTime(
      values[SignalManagementFields.DateStart].toDate(),
      values[SignalManagementFields.TimeStart],
    );
    end = mergeDateAndTime(
      values[SignalManagementFields.DateStart].toDate(),
      values[SignalManagementFields.TimeEnd],
    );
  } else {
    start = dayjs(values[SignalManagementFields.DateStart]).startOf('day').toDate();
    end = dayjs(values[SignalManagementFields.DateStart]).endOf('day').toDate();
  }
  if (
    isWeeklyPeriodSelected(values) ||
    values[SignalManagementFields.Recurrence] === RecurrenceOptions.Custom
  ) {
    recurrence = mapScheduleToRruleString(values, recurringDays) as RecurrenceOptions;
  }

  const persistConditions = values[SignalManagementFields.EnabledConditions];
  const formConditions = values[SignalManagementFields.Conditions];
  const completeRules = persistConditions
    ? pruneIncompleteConditions(formConditions?.rules)
    : [];
  const conditions = completeRules.length
    ? {
        elementType: 'conditionGroup',
        operator: formConditions?.combinator,
        conditionsElements: completeRules.map(mapUIRulesForRequest),
      }
    : null;

  const event: Omit<OncallManagementScheduleDataInput, 'oncallManagementScheduleId'> = {
    partnerId,
    primaryAgentId: values[SignalManagementFields.PrimaryAgentId],
    secondaryAgentId: values[SignalManagementFields.SecondaryAgentId]
      ? values[SignalManagementFields.SecondaryAgentId]
      : null,
    startTime: addTimeZone(start, timezone, true).toISOString(),
    endTime: addTimeZone(end, timezone, true).toISOString(),
    recurrence,
    recurrenceUntil: getRecurrenceUntil(recurrence),
    conditions: JSON.stringify(conditions),
  };
  return event;
};

const StyledDialog = styled(Dialog, {
  shouldForwardProp: (propName: PropertyKey) => propName !== 'extraWidth',
})<{ extraWidth?: boolean }>(({ extraWidth }) => ({
  [`.${dialogClasses.paper}`]: {
    width: extraWidth && '800px',
    maxWidth: extraWidth && '800px',
  },
}));

export const SignalOnCallScheduleModal = () => {
  const { modalProps } = useModalState(Modals.SignalOnCallScheduleModal);
  const { closeModal } = useModalMethods(Modals.SignalOnCallScheduleModal);

  const { isMobile } = useScreenDimensions();

  const { confirm, renderConfirmationBody } = useConfirmationModal();

  const { agents = [], timezone = '', data, partnerId, events } = modalProps || {};

  const initialEvent = useMemo(
    () => (events || []).find((it) => it.oncallManagementScheduleId === data?.id),
    [events, data?.id],
  );

  const { createOnCallScheduleEvent, isLoading: isCreating } =
    useCreateSignalOnCallScheduleEvent();
  const { updateOnCallScheduleEvent, isLoading: isUpdating } =
    useUpdateSignalOnCallScheduleEvent({
      partnerId: partnerId!,
      initialEvent,
      partnerTimezone: timezone,
    });

  const { deleteOnCallScheduleEvent } = useDeleteSignalOnCallScheduleEvent({
    partnerId: partnerId!,
    partnerTimezone: timezone,
    initialEvent,
  });

  const { showErrorAlert, showSuccessAlert } = useNotifications();

  const ruleEngineConfig = useSignalRulesEngine();

  const isEditing = Boolean(data?.id);

  const hasCustomRecurrence = data?.recurrence
    ? !Object.values(RecurrenceOptions).includes(data.recurrence)
    : false;

  const defaultFormValues = useMemo<SignalManagementFormValues>(
    () => ({
      [SignalManagementFields.PrimaryAgentId]: data?.primaryAgentId ?? '',
      [SignalManagementFields.SecondaryAgentId]: data?.secondaryAgentId ?? '',
      [SignalManagementFields.DateStart]: dayjs(data?.start),
      [SignalManagementFields.TimeStart]: data?.start
        ? dayjs(data?.start).format(TIME_FORMAT)
        : '',
      [SignalManagementFields.TimeEnd]: data?.end ? dayjs(data?.end).format(TIME_FORMAT) : '',
      [SignalManagementFields.IsAllDay]: data ? isAllDaySchedule(data) : false,
      [SignalManagementFields.Recurrence]: hasCustomRecurrence
        ? RecurrenceOptions.Custom
        : (data?.recurrence ?? RecurrenceOptions.Once),
      [SignalManagementFields.RecurrenceInterval]:
        getRecurrenceInterval(data?.recurrence)?.toString() ?? undefined,
      [SignalManagementFields.RecurrencePeriod]:
        getRecurrencePeriod(data?.recurrence) ?? undefined,
      [SignalManagementFields.EditType]: EditRecurrence.This,
      // @ts-expect-error types mismatch and have no idea how to make it happy.
      [SignalManagementFields.Conditions]:
        initialEvent && ruleEngineConfig.conditions.length > 0
          ? {
              combinator: initialEvent.conditions?.operator || 'and',
              not: false,
              rules:
                initialEvent.conditions?.conditionsElements?.map((condition) =>
                  mapConditionToFormValues(condition, ruleEngineConfig),
                ) || [],
            }
          : undefined,
      [SignalManagementFields.EnabledConditions]:
        (initialEvent?.conditions?.conditionsElements?.length || 0) > 0,
    }),
    [data, hasCustomRecurrence, initialEvent, ruleEngineConfig],
  );

  const formMethods = useForm<SignalManagementFormValues>({
    defaultValues: defaultFormValues,
  });

  const {
    watch,
    setValue,
    getValues,
    handleSubmit,
    formState: { errors, isSubmitted, isValid, isDirty },
    control,
    reset,
  } = formMethods;

  useEffect(() => {
    if (defaultFormValues && !isDirty) {
      reset(defaultFormValues, { keepDirty: true });
    }
  }, [defaultFormValues, reset, isDirty]);

  const watchableDateStart = watch(SignalManagementFields.DateStart);
  const watchablePrimaryAgentId = watch(SignalManagementFields.PrimaryAgentId);
  const watchableRecurrence = watch(SignalManagementFields.Recurrence);
  const watchableRecurrencePeriod = watch(SignalManagementFields.RecurrencePeriod);
  const watchableIsAllDay = watch(SignalManagementFields.IsAllDay);
  const watchableEditType = watch(SignalManagementFields.EditType);
  const watchableEnabledConditions = watch(SignalManagementFields.EnabledConditions);

  const [isLoading, setIsLoading] = useState(false);
  const [deleteChoice, setDeleteChoice] = useState<DeleteRecurrence>(DeleteRecurrence.This);
  const [isRecurringDeleteFlow, setIsRecurringDeleteFlow] = useState(false);
  const deleteChoiceRef = useRef<DeleteRecurrence>(DeleteRecurrence.This);

  const [recurringDays, setRecurringDays] = useState<Weekday[]>(() =>
    getRecurrenceDays(data?.recurrence),
  );

  const onClose = useCallback(() => {
    closeModal();
    modalProps?.onClose?.();
  }, [closeModal, modalProps]);

  const onSubmit = useCallback(
    async (values: SignalManagementFormValues) => {
      setIsLoading(true);

      const event = formDataToOnCallManagementScheduleDataInput({
        values,
        timezone,
        partnerId: partnerId!,
        recurringDays,
      });

      try {
        if (isEditing) {
          const result = await updateOnCallScheduleEvent(
            { ...event, oncallManagementScheduleId: data?.id! },
            canEditCurrentEvent(data) ? values[SignalManagementFields.EditType] : undefined,
          );

          if (result) {
            showSuccessAlert('Event was updated successfully');
            if (result.updatedEvent) {
              modalProps?.onSubmit?.(result.updatedEvent, true);
            }
            result.createdEvents.forEach((createdEvent) => {
              modalProps?.onSubmit?.(createdEvent, false);
            });
            if (result.deletedEventId) {
              modalProps?.onDelete?.(result.deletedEventId);
            }
          }
        } else {
          const response = await createOnCallScheduleEvent(event);

          if (response) {
            showSuccessAlert('Event was created successfully');
            modalProps?.onSubmit?.(response, false);
          }
        }
        onClose();
      } finally {
        setIsLoading(false);
      }
    },
    [
      createOnCallScheduleEvent,
      data,
      isEditing,
      modalProps,
      onClose,
      partnerId,
      recurringDays,
      showSuccessAlert,
      timezone,
      updateOnCallScheduleEvent,
    ],
  );

  const handleDeleteWithMode = useCallback(
    async (deleteMode: DeleteRecurrence) => {
      if (!data?.id) {
        return;
      }

      setIsLoading(true);
      try {
        const result = await deleteOnCallScheduleEvent(
          {
            ...formDataToOnCallManagementScheduleDataInput({
              values: getValues(),
              timezone,
              partnerId: partnerId!,
              recurringDays,
            }),
            oncallManagementScheduleId: data.id,
          },
          deleteMode,
        );

        if (result?.updatedEvent) {
          modalProps?.onSubmit?.(result.updatedEvent, true);
        }

        if (result?.createdEvent) {
          modalProps?.onSubmit?.(result.createdEvent, false);
        }

        if (result?.deletedEventId) {
          modalProps?.onDelete?.(result.deletedEventId);
        }

        onClose();
      } catch {
        showErrorAlert('Something went wrong deleting schedule event, please try again');
      } finally {
        setIsLoading(false);
      }
    },
    [
      data?.id,
      deleteOnCallScheduleEvent,
      getValues,
      modalProps,
      onClose,
      partnerId,
      recurringDays,
      showErrorAlert,
      timezone,
    ],
  );

  const handleDeleteSingleEvent = useCallback(async () => {
    const confirmed = await confirm({
      body: 'Please confirm you want to delete this event.\nThis process cannot be undone.',
      variant: 'delete',
    });

    if (!confirmed) {
      return;
    }

    await handleDeleteWithMode(DeleteRecurrence.ThisAndFollowing);
  }, [confirm, handleDeleteWithMode]);

  const setDeleteChoiceValue = useCallback((value: DeleteRecurrence) => {
    deleteChoiceRef.current = value;
    setDeleteChoice(value);
  }, []);

  const handleDeleteRecurringEvent = useCallback(async () => {
    setDeleteChoiceValue(DeleteRecurrence.This);
    setIsRecurringDeleteFlow(true);

    const confirmed = await confirm({
      title: 'Delete recurring event?',
      body: 'This event is part of a recurring schedule. Choose what you want to delete.',
      variant: 'delete',
    });

    setIsRecurringDeleteFlow(false);

    if (!confirmed) {
      return;
    }

    await handleDeleteWithMode(deleteChoiceRef.current);
  }, [confirm, handleDeleteWithMode, setDeleteChoiceValue]);

  const handleDeleteClick = useCallback(async () => {
    if (!data?.id) {
      return;
    }

    const isRecurringEvent = !!data.recurrence && data.recurrence !== RecurrenceOptions.Once;

    if (!isRecurringEvent) {
      await handleDeleteSingleEvent();
      return;
    }

    await handleDeleteRecurringEvent();
  }, [data?.id, data?.recurrence, handleDeleteRecurringEvent, handleDeleteSingleEvent]);

  const onRecurringDayChange = useCallback(
    (day: (typeof WEEKDAYS)[number]) => () => {
      if (recurringDays.includes(day.value)) {
        setRecurringDays((prev) => prev.filter((d) => d !== day.value));
      } else {
        setRecurringDays((prev) => [...prev, day.value]);
      }
    },
    [recurringDays],
  );

  const onAllDayCheckChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setValue(
        SignalManagementFields.TimeStart,
        event.target.checked ? '' : watchableDateStart.format(TIME_FORMAT),
      );
      setValue(
        SignalManagementFields.TimeEnd,
        event.target.checked ? '' : watchableDateStart.add(30, 'minute').format(TIME_FORMAT),
      );
    },
    [setValue, watchableDateStart],
  );

  const onRecurrenceChange = useCallback(
    (event: SelectChangeEvent<unknown>) => {
      const selectedRecurrence = event.target.value as RecurrenceOptions;
      setValue(SignalManagementFields.Recurrence, selectedRecurrence);

      if (selectedRecurrence === RecurrenceOptions.Custom) {
        setValue(SignalManagementFields.RecurrenceInterval, '1');
        setValue(SignalManagementFields.RecurrencePeriod, Frequency.DAILY);
      } else {
        setValue(SignalManagementFields.RecurrenceInterval, undefined);
        setValue(SignalManagementFields.RecurrencePeriod, undefined);
      }

      if (!isWeeklyPeriodSelected({ recurrence: selectedRecurrence })) {
        setRecurringDays([]);
      } else {
        /*
          If user selects weekly recurrence, we should pre-select current day
          to build proper RRule that would allow easier schedule editing.
        */
        if (recurringDays.length === 0) {
          const currentDay = watchableDateStart.day();
          setRecurringDays([WEEKDAYS[currentDay].value]);
        }
      }
    },
    [setValue, recurringDays.length, watchableDateStart],
  );

  const onEditRecurrence = useCallback(
    (event: SelectChangeEvent<unknown>) => {
      setValue(SignalManagementFields.EditType, event.target.value as EditRecurrence);
    },
    [setValue],
  );

  const agentsOptions = useMemo(
    () =>
      agents.map((it) => ({
        id: it.id,
        label: getFullName(it),
        value: it.id,
      })),
    [agents],
  );

  const secondaryAgentsUsersPool = useMemo(() => {
    if (!watchablePrimaryAgentId) {
      return agentsOptions;
    }

    return agentsOptions.filter((it) => it.id !== watchablePrimaryAgentId);
  }, [agentsOptions, watchablePrimaryAgentId]);

  const isCustomRecurrenceSelected = watchableRecurrence === RecurrenceOptions.Custom;
  const isOneTimeEvent = watchableRecurrence === RecurrenceOptions.Once;

  const formSelectCommonProps = {
    type: 'select' as const,
    variant: 'outlined' as const,
    size: 'small' as const,
  };

  const conditions = ruleEngineConfig.conditions.filter((c) =>
    c.triggers.some((t) =>
      [BehaviorTrigger.ConversationCreated, BehaviorTrigger.ConversationUpdated].includes(t),
    ),
  );

  const content = (
    <Stack spacing={2} mt={2}>
      <FormProvider {...formMethods}>
        <FormSelect
          required
          {...formSelectCommonProps}
          data-testid="signal-modal-primary-agent"
          name={SignalManagementFields.PrimaryAgentId}
          label="Primary On-Call User"
          options={agentsOptions}
        />
        <FormSelect
          {...formSelectCommonProps}
          data-testid="signal-modal-secondary-agent"
          name={SignalManagementFields.SecondaryAgentId}
          label="Secondary On-Call User"
          options={secondaryAgentsUsersPool}
          clearable
        />
        <FormGroup />
        <Stack width="100%" spacing={2} justifyContent="space-between">
          <Stack direction="row" gap={1} alignItems="flex-start">
            <FormDatePicker
              required
              type="datepicker"
              name={SignalManagementFields.DateStart}
              label="Starting On"
              size="small"
              datePickerProps={{
                slotProps: {
                  textField: { size: 'small' },
                },
              }}
              showErrorText
              rules={{
                validate: (_: string, formValues: SignalManagementFormValues) =>
                  validateOnCallSchedule(formValues)[SignalManagementFields.DateStart] || true,
              }}
            />
            <FormCheckbox
              name={SignalManagementFields.IsAllDay}
              label="All day"
              sx={{ marginLeft: '0.5rem', whiteSpace: 'nowrap' }}
              checkboxProps={{
                onChange: onAllDayCheckChange,
              }}
              rules={{
                deps: SignalManagementFields.DateStart,
              }}
            />
          </Stack>
          {!watchableIsAllDay && (
            <Stack direction="row" gap={1}>
              <FormSelect
                required={!watchableIsAllDay}
                {...formSelectCommonProps}
                data-testid="signal-modal-time-start"
                name={SignalManagementFields.TimeStart}
                label="From"
                options={mapOptions(TIME_SLOT_OPTIONS)}
                sx={{ width: '50%' }}
                rules={{
                  deps: [SignalManagementFields.DateStart, SignalManagementFields.TimeEnd],
                  validate: (_: string, formValues: SignalManagementFormValues) =>
                    validateOnCallSchedule(formValues)[SignalManagementFields.TimeStart] ||
                    true,
                }}
              />
              <FormSelect
                required={!watchableIsAllDay}
                {...formSelectCommonProps}
                data-testid="signal-modal-time-end"
                name={SignalManagementFields.TimeEnd}
                label="To"
                options={mapOptions(TIME_SLOT_OPTIONS)}
                sx={{ width: '50%' }}
                rules={{
                  deps: [SignalManagementFields.DateStart, SignalManagementFields.TimeStart],
                  validate: (_: string, formValues: SignalManagementFormValues) =>
                    validateOnCallSchedule(formValues)[SignalManagementFields.TimeEnd] || true,
                }}
              />
            </Stack>
          )}
          {!!errors[SignalManagementFields.TimeEnd]?.message && (
            <FormHelperText error sx={{ flexGrow: 1, mt: 1, ml: 1 }}>
              {errors[SignalManagementFields.TimeEnd]?.message}
            </FormHelperText>
          )}
        </Stack>
        {canEditCurrentEvent(data) && watchableRecurrence !== RecurrenceOptions.Once && (
          <FormSelect
            {...formSelectCommonProps}
            data-testid="signal-modal-edit-type"
            name={SignalManagementFields.EditType}
            label="Edit recurring schedule"
            options={mapOptions(UNTIL_SLOTS)}
            selectProps={{
              onChange: onEditRecurrence,
            }}
          />
        )}
        {(!isEditing ||
          (canEditCurrentEvent(data) && watchableEditType !== EditRecurrence.This)) && (
          <>
            <FormSelect
              {...formSelectCommonProps}
              data-testid="signal-modal-recurrence"
              name={SignalManagementFields.Recurrence}
              label="Recurrence"
              options={mapOptions(RECURRENCE_OPTIONS)}
              selectProps={{
                onChange: onRecurrenceChange,
              }}
            />
            {isCustomRecurrenceSelected ? (
              <Stack spacing={3}>
                <Stack
                  direction="row"
                  alignItems="center"
                  columnGap={1.5}
                  rowGap={1}
                  flexWrap="wrap"
                >
                  <Typography sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}>
                    Repeat every
                  </Typography>
                  <Controller
                    control={control}
                    name={SignalManagementFields.RecurrenceInterval}
                    render={({ field }) => (
                      <Autocomplete
                        data-testid="signal-modal-interval"
                        disableClearable
                        openOnFocus
                        options={RECURRENCE_INTERVAL_OPTIONS.map((option) => option.label)}
                        value={field.value ?? '1'}
                        onChange={(_, value) => field.onChange(value)}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Interval"
                            variant={formSelectCommonProps.variant}
                            size={formSelectCommonProps.size}
                          />
                        )}
                        sx={{ flexShrink: 0, width: '5.5rem' }}
                      />
                    )}
                  />
                  <FormSelect
                    {...formSelectCommonProps}
                    data-testid="signal-modal-period"
                    name={SignalManagementFields.RecurrencePeriod}
                    label="Period"
                    options={mapOptions(RECURRENCE_PERIOD_OPTIONS)}
                    sx={{ flex: '0 0 9rem' }}
                  />
                </Stack>
              </Stack>
            ) : null}
            {!isOneTimeEvent &&
              isWeeklyPeriodSelected({
                recurrence: watchableRecurrence,
                recurrencePeriod: watchableRecurrencePeriod,
              }) && (
                <Stack gap={1}>
                  <Typography>Repeat on</Typography>
                  <ToggleButtonGroup size="small" value={recurringDays}>
                    {WEEKDAYS.map((day) => (
                      <StyledToggleButton
                        data-testid={`signal-modal-weekday-${day.label}`}
                        isMobile={isMobile}
                        key={day.label}
                        value={day.value}
                        onClick={onRecurringDayChange(day)}
                      >
                        {day.label}
                      </StyledToggleButton>
                    ))}
                  </ToggleButtonGroup>
                </Stack>
              )}
          </>
        )}
        <FormCheckbox
          name={SignalManagementFields.EnabledConditions}
          label="Filter by conditions"
          sx={{ marginLeft: '0.5rem' }}
          rules={{
            deps: SignalManagementFields.Conditions,
          }}
        />
        {errors[SignalManagementFields.Conditions]?.message ? (
          <FormHelperText
            error
            data-testid="signal-modal-conditions-error"
            sx={{ ml: 1, mt: 0 }}
          >
            {errors[SignalManagementFields.Conditions]?.message}
          </FormHelperText>
        ) : null}

        {watchableEnabledConditions ? (
          <Controller
            control={control}
            name={SignalManagementFields.Conditions}
            rules={{
              validate: (
                value: SignalManagementFormValues[SignalManagementFields.Conditions],
                formValues: SignalManagementFormValues,
              ) =>
                !formValues[SignalManagementFields.EnabledConditions] ||
                getValidConditionsCount(value) > 0 ||
                'Add at least one complete condition to save this schedule',
            }}
            render={({ field }) => (
              <QueryBuilderMaterial muiComponents={muiComponents}>
                <RulesFormContainer dense>
                  <QueryBuilder
                    enableMountQueryChange={false}
                    autoSelectField
                    resetOnOperatorChange
                    resetOnFieldChange
                    context={{
                      timezone: ruleEngineConfig.timezone,
                      queryOptions: ruleEngineConfig.queryOptions,
                    }}
                    showCombinatorsBetweenRules
                    query={field.value}
                    onQueryChange={field.onChange}
                    fields={conditions as unknown as FullField[]}
                    // @ts-expect-error Overriding default library typings because of custom fields structure.
                    getOperators={(field: SelectorValue) => {
                      return field?.operators || [];
                    }}
                    // @ts-expect-error Overriding default library typings because of custom fields structure.
                    controlElements={conditionsCustomControls}
                    controlClassnames={{
                      queryBuilder: 'queryBuilder-justified',
                    }}
                  />
                </RulesFormContainer>
              </QueryBuilderMaterial>
            )}
          />
        ) : null}
      </FormProvider>
    </Stack>
  );

  const submitButtonIsLoading = isCreating || isUpdating || isLoading;

  return (
    <StyledDialog
      open
      data-testid="signal-modal"
      onClose={onClose}
      fullWidth
      extraWidth={watchableEnabledConditions}
      PaperProps={{
        sx: {
          marginTop: '20vh',
          alignSelf: 'flex-start',
          ...(isMobile && {
            width: '100%',
            height: '100%',
            maxWidth: '100%',
            borderRadius: 0,
            maxHeight: '100%',
            margin: 0,
          }),
        },
      }}
    >
      <DialogTitle
        sx={{
          borderBottom: '1px solid',
          borderColor: (theme) => theme.palette.custom.OV.sys.outlineVariant,
        }}
      >
        <Stack direction="row" spacing={2}>
          <Typography variant="overline">
            {isEditing ? 'Edit On-Call Schedule' : 'Add On-Call Schedule'}
          </Typography>
        </Stack>
      </DialogTitle>
      <DialogContent
        sx={{
          maxHeight: isMobile ? 'auto' : '60vh',
          overflowY: 'auto',
        }}
      >
        {content}
      </DialogContent>
      <DialogActions>
        {!!isMobile && (
          <Button disabled={submitButtonIsLoading} onClick={onClose}>
            Cancel
          </Button>
        )}
        {!!isEditing && (
          <Button
            data-testid="signal-modal-delete"
            disabled={isLoading}
            color="error"
            onClick={handleDeleteClick}
          >
            Delete
          </Button>
        )}
        <LoadingButton
          data-testid="signal-modal-save"
          onClick={handleSubmit(onSubmit)}
          disabled={submitButtonIsLoading || (isSubmitted && !isValid)}
          loading={submitButtonIsLoading}
          sx={{
            color: (theme) => theme.palette.custom.OV.sys.primary,
          }}
        >
          Save
        </LoadingButton>
      </DialogActions>
      {isRecurringDeleteFlow
        ? renderConfirmationBody(
            <RadioGroup
              name="recurring-delete-choice"
              value={deleteChoice}
              sx={{ px: 2 }}
              aria-label="Recurring delete scope"
              onChange={(event) =>
                setDeleteChoiceValue(event.target.value as DeleteRecurrence)
              }
            >
              <FormControlLabel
                value={DeleteRecurrence.This}
                control={<Radio />}
                label="Only this event"
              />
              <FormControlLabel
                value={DeleteRecurrence.ThisAndFollowing}
                control={<Radio />}
                label="This and all following events"
              />
            </RadioGroup>,
          )
        : null}
    </StyledDialog>
  );
};
